import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Shield, ShieldCheck, Trash2, Plus, Loader2, AlertCircle, Smartphone } from "lucide-react";
import { useMFA } from "@/contexts/MFAContext";
import { EnrollMFA } from "./EnrollMFA";
import { logger } from "@/lib/logger";
import { toast } from "@/hooks/use-toast";

export function MFASettings() {
  const { enrolledFactors, hasEnrolledTOTP, refreshMFAStatus, currentAAL } = useMFA();

  const [isAddingFactor, setIsAddingFactor] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string>("");

  const totpFactors = enrolledFactors.filter(
    (f) => f.factor_type === "totp" && f.status === "verified"
  );

  const handleRemoveFactor = async (factorId: string) => {
    // Don't allow removing the last factor
    if (totpFactors.length <= 1) {
      toast({
        variant: "destructive",
        title: "Cannot remove",
        description: "You must have at least one authenticator enabled. MFA is required for this application.",
      });
      return;
    }

    setIsRemoving(factorId);
    setError("");

    try {
      logger.debug("MFASettings: Unenrolling factor:", factorId);

      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId,
      });

      if (unenrollError) {
        logger.error("MFASettings: Unenroll error:", unenrollError);
        throw unenrollError;
      }

      logger.debug("MFASettings: Factor unenrolled successfully");

      // Refresh the session to update AAL
      await supabase.auth.refreshSession();
      await refreshMFAStatus();

      toast({
        title: "Authenticator removed",
        description: "The authenticator has been removed from your account.",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to remove authenticator";
      setError(message);
      toast({
        variant: "destructive",
        title: "Error",
        description: message,
      });
    } finally {
      setIsRemoving(null);
    }
  };

  const handleEnrollmentComplete = async () => {
    setIsAddingFactor(false);
    await refreshMFAStatus();
    toast({
      title: "Authenticator added",
      description: "Your new authenticator has been set up successfully.",
    });
  };

  if (isAddingFactor) {
    return (
      <EnrollMFA
        onEnrolled={handleEnrollmentComplete}
        onCancel={() => setIsAddingFactor(false)}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-500" />
          <CardTitle className="text-lg">Two-Factor Authentication</CardTitle>
        </div>
        <CardDescription>
          Manage your authenticator apps for secure sign-in
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <Badge variant={hasEnrolledTOTP ? "default" : "destructive"} className="gap-1">
            <Shield className="h-3 w-3" />
            {hasEnrolledTOTP ? "MFA Enabled" : "MFA Required"}
          </Badge>
          {currentAAL === "aal2" && (
            <Badge variant="outline" className="gap-1 text-green-600">
              <ShieldCheck className="h-3 w-3" />
              Verified this session
            </Badge>
          )}
        </div>

        {/* Enrolled Factors List */}
        {totpFactors.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">Your authenticators:</p>
            {totpFactors.map((factor) => (
              <div
                key={factor.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {factor.friendly_name || "Authenticator App"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Added {new Date(factor.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={isRemoving === factor.id || totpFactors.length <= 1}
                    >
                      {isRemoving === factor.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove authenticator?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {totpFactors.length <= 1 ? (
                          "You cannot remove your only authenticator. MFA is required for this application."
                        ) : (
                          "This will remove this authenticator from your account. You will still have other authenticators enabled."
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      {totpFactors.length > 1 && (
                        <AlertDialogAction
                          onClick={() => handleRemoveFactor(factor.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove
                        </AlertDialogAction>
                      )}
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No authenticators configured. Please set up an authenticator to secure your account.
            </AlertDescription>
          </Alert>
        )}

        {/* Add Another Factor Button */}
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={() => setIsAddingFactor(true)}
        >
          <Plus className="h-4 w-4" />
          Add another authenticator
        </Button>

        {/* Info text */}
        <p className="text-xs text-muted-foreground">
          We recommend adding a backup authenticator in case you lose access to your primary device.
          The required permissions have full access to your forms, so MFA is required for all users.
        </p>
      </CardContent>
    </Card>
  );
}
